import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/v2/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { cn } from "@/lib/utils/cn";
import { calculateVisiblePages } from "@/lib/utils/pagination";

interface TablePaginatorProps {
  isLoading?: boolean;
  itemsPerPage: number;
  page: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage?: (itemsPerPage: number) => void;
  totalItems: number;
}

const ITEMS_PER_PAGE_OPTIONS = [
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
] as const;

function calculateItemRange(
  page: number,
  itemsPerPage: number,
  totalItems: number
) {
  const itemOffset = (page - 1) * itemsPerPage;
  const startItem = itemOffset + 1;
  const endItem = Math.min(itemOffset + itemsPerPage, totalItems);
  return { startItem, endItem };
}

export function TablePaginator({
  isLoading,
  itemsPerPage,
  page,
  setCurrentPage,
  setItemsPerPage,
  totalItems,
}: TablePaginatorProps) {
  const { t } = useTranslation();

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const { startItem, endItem } = calculateItemRange(
    page,
    itemsPerPage,
    totalItems
  );
  const visiblePages = useMemo(
    () => calculateVisiblePages(page, totalPages),
    [page, totalPages]
  );

  const handlePageChange = (newPage: number) => {
    if (isLoading || newPage < 1 || newPage > totalPages) {
      return;
    }
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (value: string) => {
    if (!setItemsPerPage) return;

    setItemsPerPage(Number(value));
    // NOTE: setPageSize in usePageSizeSearchParams already sets page to 1,
    // so we don't need to call setCurrentPage(1) here to avoid race condition
  };

  const handlePreviousClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handlePageChange(page - 1);
  };

  const handleNextClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handlePageChange(page + 1);
  };

  const handlePageClick = (pageNum: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    handlePageChange(pageNum);
  };

  if (isLoading && totalItems === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4 w-full">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-9 w-48" />
      </div>
    );
  }

  if (totalItems === 0) {
    return null;
  }

  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between w-full">
      {/* Pagination Controls */}
      {totalPages > 0 && (
        <Pagination className="mx-0 w-full sm:w-auto sm:flex-1">
          <PaginationContent className="justify-start w-full sm:w-auto">
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={handlePreviousClick}
                className={cn(
                  isFirstPage && "pointer-events-none opacity-50",
                  "cursor-pointer"
                )}
                aria-disabled={isFirstPage}
              >
                <span className="hidden sm:inline">{t("common.previous")}</span>
              </PaginationPrevious>
            </PaginationItem>

            {visiblePages.map((pageItem, index) => {
              if (pageItem === "ellipsis") {
                return (
                  <PaginationItem key={`ellipsis-${index}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }

              const isActive = page === pageItem;
              return (
                <PaginationItem key={pageItem}>
                  <PaginationLink
                    href="#"
                    onClick={handlePageClick(pageItem)}
                    isActive={isActive}
                    className={cn(
                      "cursor-pointer",
                      isActive && "pointer-events-none"
                    )}
                  >
                    {pageItem}
                  </PaginationLink>
                </PaginationItem>
              );
            })}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={handleNextClick}
                className={cn(
                  isLastPage && "pointer-events-none opacity-50",
                  "cursor-pointer"
                )}
                aria-disabled={isLastPage}
              >
                <span className="hidden sm:inline">{t("common.next")}</span>
              </PaginationNext>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {/* Items Per Page Selector and Info */}
      <div className="flex items-center gap-2 text-sm">
        {isLoading ? (
          <Skeleton className="h-9 w-48" />
        ) : (
          <>
            <span className="text-muted-foreground whitespace-nowrap">
              {t("pagination.itemsDisplayPrefix", { start: startItem })}
            </span>
            {setItemsPerPage && (
              <Select
                value={itemsPerPage.toString()}
                onValueChange={handleItemsPerPageChange}
              >
                <SelectTrigger size="sm" className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value.toString()}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <span className="text-muted-foreground whitespace-nowrap">
              {t("pagination.itemsDisplay", {
                start: startItem,
                end: endItem,
                total: totalItems,
              })}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
